import { FullScreenDocument, FullScreenElement } from "@components/PdfViewer/types";

export const isElementVisible = (child: Element, parent: Element): boolean => {
  const childRect = child.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  // Check if child is fully or partially within the parent
  const isVisible =
    childRect.top < parentRect.bottom &&
    childRect.bottom > parentRect.top &&
    childRect.left < parentRect.right &&
    childRect.right > parentRect.left;

  return isVisible;
};

export const getVisibleHeight = (child: Element, parent: Element): number => {
  const { bottom: childBottom, top: childTop } = child.getBoundingClientRect();
  const { bottom: parentBottom, top: parentTop } = parent.getBoundingClientRect();

  return Math.max(0, Math.min(childBottom, parentBottom) - Math.max(childTop, parentTop));
};

export const mostVisiblePageReducer = (
  prevPage: Element,
  currentPage: Element,
  documentContainer: Element,
): Element => {
  const prevHeight = getVisibleHeight(prevPage, documentContainer);
  const currentHeight = getVisibleHeight(currentPage, documentContainer);

  return prevHeight > currentHeight ? prevPage : currentPage;
};

export function isFullScreenSupported(el: FullScreenElement | null): boolean {
  if (!el) return false;

  return (
    !!el.requestFullscreen ||
    !!el.msRequestFullscreen || // IE/Edge
    !!el.mozRequestFullscreen || // Firefox
    !!el.webkitRequestFullscreen // Chrome, Safari and Opera
  );
}

export const enterFullScreen = (el: FullScreenElement): void => {
  try {
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    } else if (el.mozRequestFullscreen) {
      el.mozRequestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to enter fullscreen:", error);
  }
};

export const exitFullScreen = (doc: FullScreenDocument): void => {
  try {
    if (doc.exitFullscreen) {
      doc.exitFullscreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to exit fullscreen:", error);
  }
};

export const getMostVisiblePageNumber = (container: Element): number | null => {
  const pages = container.querySelectorAll(".page");
  const visiblePages = Array.from(pages).filter((page) => isElementVisible(page, container));

  if (!visiblePages.length) return null;

  // Page that has the most visible height in the document container
  const mostVisiblePageNumber = visiblePages
    .reduce((prevPage, currentPage) => mostVisiblePageReducer(prevPage, currentPage, container))
    ?.getAttribute("data-page-number");

  return mostVisiblePageNumber ? Number(mostVisiblePageNumber) : null;
};
